import { getLists, getListsQuick } from "../../../../../api/ContactAPI";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Anchor,
  Flex,
  Group,
  HoverCard,
  List,
  Loader,
  NumberFormatter,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import MainTable from "../../../../../layouts/Table/MainTable";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import numberColumn from "../../../../../layouts/Table/columns/numberColumn";
import { useListingLoader } from "../../../../../components/hooks/ListingHooks";
import { useListStore } from "../../../../../stores/ListStore";
import dateColumn from "../../../../../layouts/Table/columns/dateColumn";
import {
  IconBuildingCommunity,
  IconCircleFilled,
  IconExternalLink,
  IconUsers,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useCampaignStore } from "../../../../../stores/CampaignStore";
import { IconEmail } from "../../../../../assets/icons/IconEmail";
import { IconLinkedin } from "../../../../../components/Icons/IconLinkedin";

const ContactListsListingTable = ({
  selectionModel,
  onSelectionChange,
  isAudience,
}) => {
  const apiRef = useGridApiRef();
  const lang = useLanguage((s) => s.language);
  const oldProgramData = useCampaignStore((s) => s.oldProgramData);
  const programData = useCampaignStore((s) => s.programData);

  const isReprog = useMemo(() => {
    let params = new URLSearchParams(location.search);
    let reprog = params.get("reprogram");

    return !!reprog;
  }, []);

  const columns = useMemo(() => {
    let result = [
      numberColumn(),
      {
        field: "name",
        headerName: lang.campaign.list,
        flex: 1,
        renderCell: ({ row, value }) => {
          return (
            <Group>
              <Text fw={500} size="lg">
                {value}
              </Text>

              {(row.state_details?.find((x) => x.state === "InProgress") ||
                row.state_details?.find((x) => x.state === "InQueue")) && (
                <Tooltip
                  label={
                    lang.contact_lists.jobs.in_progress
                      .loading_indicator_tooltip
                  }
                >
                  <Loader color="blue" type="dots" size={"xs"} />
                </Tooltip>
              )}
            </Group>
          );
        },
      },
      {
        field: "used_in_programmations",
        headerName: lang.workflows.columns.programmed.name,
        width: 150,
        renderCell: ({ value }) => {
          if (value.length > 0) {
            return (
              <HoverCard position="top" withArrow offset={0}>
                <HoverCard.Target>
                  <Text c={"tertiary.8"} fw={600} fz={12}>
                    {lang.workflows.columns.programmed.true} ( {value.length} )
                  </Text>
                </HoverCard.Target>

                <HoverCard.Dropdown>
                  <Stack>
                    {value?.map((item) => (
                      <Group key={item.id} gap={"xs"}>
                        <IconCircleFilled size={6} color="black" />
                        <Anchor
                          size="sm"
                          target="_blank"
                          fw={600}
                          href={`/campaign/edit/${item.id}/workflow/${item.workflow_id}`}
                        >
                          {item?.workflow_name} #{item.id}
                        </Anchor>
                      </Group>
                    ))}
                  </Stack>
                </HoverCard.Dropdown>
              </HoverCard>
            );
          }
          return (
            <Text c={"red"} fw={600} fz={12}>
              {lang.workflows.columns.programmed.false}
            </Text>
          );
        },
      },
      {
        field: "number_of_contacts",
        headerName: "Contacts",
        width: 100,
      },
      {
        field: "number_of_emails",
        headerName: "Emails",
        width: 100,
      },
      {
        field: "number_of_linkedin_url",
        headerName: "LinkedIn URL",
        width: 100,
      },
      dateColumn(),
    ];

    return result;
  }, [apiRef]);

  const loading = useListStore((s) => s.listingLoad);
  const setLoading = useListStore((s) => s.setListingLoad);
  const [rows, setRows] = useState([]);

  const actionsCell = useCallback(
    ({ row }) => (
      <ActionIcon
        component={Link}
        to={`/contact-lists/${row.id}`}
        variant="light"
        size={"lg"}
        className="to-blue"
        target="_blank"
      >
        <IconExternalLink />
      </ActionIcon>
    ),
    [],
  );

  const listStatsMap = useMemo(() => {
    return [
      {
        label: "Contacts",
        value: "number_of_contacts",
        icon: (
          <ThemeIcon variant="light" color="primary" size={"md"}>
            <IconUsers size={18} />
          </ThemeIcon>
        ),
      },
      {
        label: "Emails",
        value: "number_of_emails",
        icon: (
          <ThemeIcon variant="light" color="orange.4" size={"md"}>
            <IconEmail size={18} />
          </ThemeIcon>
        ),
      },
      {
        label: "LinkedIn URL",
        value: "number_of_linkedin_url",
        icon: (
          <ThemeIcon variant="light" color="blue.4" size={"md"}>
            <IconLinkedin size={18} />
          </ThemeIcon>
        ),
      },
    ];
  }, []);

  const [listStats, setListStats] = useState({});
  const handleSelection = (selectionModel) => {
    onSelectionChange(selectionModel);

    const selectedRows = rows.filter((x) =>
      selectionModel.find((selectedId) => selectedId === x.id),
    );
    const result = selectedRows.reduce(
      (acc, item) => ({
        ...acc,
        ...Object.fromEntries(
          Object.entries(item)
            .filter(([k]) => k.startsWith("number_"))
            .map(([k, v]) => [k, (acc[k] || 0) + v]),
        ),
      }),
      {},
    );

    setListStats(result);
    useCampaignStore.getState().setProgramData({
      ...useCampaignStore.getState().programData,
      listStats: result,
    });
  };

  const handleRowClick = ({ row }) => {
    let newSelection = selectionModel?.includes(row.id)
      ? selectionModel.filter((x) => x !== row.id)
      : [...selectionModel, row.id];
    onSelectionChange(newSelection);

    handleSelection(newSelection);
  };

  const isRowSelectable = useCallback(
    ({ id }) => {
      // if (isAudience && programData?.contactlist_ids?.length > 0) {
      //   return false;
      // }
      if (isReprog) {
        return true;
      }
      return oldProgramData
        ? !oldProgramData.contactlist_ids?.includes(id)
        : true;
    },
    [isReprog, oldProgramData],
  );

  useEffect(() => {
    setLoading(true);
  }, []);

  useEffect(() => {
    if (!loading) {
      handleSelection(useCampaignStore.getState().programData?.contactlist_ids);
    }
  }, [loading, rows]);

  useListingLoader({
    loading,
    setLoading,
    listGetter: getListsQuick,
    responseField: "contact_lists",
    setRows,
    getRows: (result) => {
      if (programData) {
        return result.sort((a, b) => {
          const aIncluded = programData?.contactlist_ids?.includes(a.id);
          const bIncluded = programData?.contactlist_ids?.includes(b.id);

          // Objects with IDs in priorityIds come first
          if (aIncluded && !bIncluded) return -1;
          if (!aIncluded && bIncluded) return 1;

          // Keep the relative order otherwise
          return b.id - a.id;
        });
      }
      return result.sort((a, b) => b.id - a.id);
    },
  });

  return (
    <Flex w={"100%"} pos={"relative"} h={"75vh"}>
      <Group
        gap={"xl"}
        pos={"absolute"}
        top={12}
        right={96}
        style={{ zIndex: 300 }}
      >
        {listStatsMap.map((item) => (
          <Group gap={6} key={item.value}>
            <Tooltip label={item.label}>{item.icon}</Tooltip>

            <Text size="md" fw={500}>
              {listStats[item.value] ? (
                <NumberFormatter value={listStats[item.value]} />
              ) : (
                "-"
              )}
            </Text>
          </Group>
        ))}

        <Group gap={6}>
          <Tooltip label={lang.prm.company}>
            <ThemeIcon variant="light" color="violet.4" size={"md"}>
              <IconBuildingCommunity size={18} />
            </ThemeIcon>
          </Tooltip>

          <Text size="md" fw={500}>
            {listStats?.["number_of_companies"] ? (
              <NumberFormatter value={listStats?.["number_of_companies"]} />
            ) : (
              "-"
            )}
          </Text>
        </Group>
      </Group>
      <MainTable
        apiRef={apiRef}
        columns={columns}
        rows={rows}
        loading={loading}
        setLoading={setLoading}
        id={"list-table"}
        disableGotoPage
        defaultPerPage={15}
        disablePerPage
        onRowClick={handleRowClick}
        whiteButtons
        withSorter
        actionsCell={actionsCell}
        tableSx={{
          "& .MuiDataGrid-cell": {
            border: "none !important",
            // borderRadius: 8,
          },
          "& .MuiDataGrid-main": {
            border: "none !important",
            borderRadius: 3,
            background: "#fff",
          },
          "& .table-row": {
            cursor: "pointer",
            // borderRadius: 4,
          },
          "& .MuiDataGrid-pinnedColumns--right .table-row": {
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderLeft: "none",
          },
          "& .MuiDataGrid-pinnedColumns--left .table-row": {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderRight: "none",
          },
          "& .MuiDataGrid-virtualScrollerRenderZone .table-row": {
            borderRadius: 0,
            borderTopRightRadius: "0 !important",
            borderBottomRightRadius: "0 !important",
          },
          "& .MuiDataGrid-selectedRowCount": {
            visibility: "hidden",
          },
          "& .MuiPagination-root": {
            left: 0,
            transform: "none",
          },
          "& .Mui-selected": {
            color: "",
          },
          "& .MuiDataGrid-footerContainer": {
            border: 0,
          },
        }}
        onSelectionChange={handleSelection}
        selectionModel={selectionModel}
        autofocusSearch
        isRowSelectable={isRowSelectable}
      />
    </Flex>
  );
};

export default ContactListsListingTable;
